import {
    roundVec2,
} from "./colors";
import type {
    DiagramNode,
    SymmetryAxis,
    SymmetryState,
    Vec2,
} from "./types";

// --- symmetry -------------------------------------------------------------
// New nodes/connections mirror across the vertical ("x") and/or horizontal
// ("y") centerline of the canvas, rather than around the world origin — that
// reads naturally on a fixed-size 2D canvas.


function mirrorVec(v: Vec2, axis: SymmetryAxis, center: Vec2): Vec2 {
    const mirrored =
        axis === "x"
            ? { x: 2 * center.x - v.x, y: v.y }
            : { x: v.x, y: 2 * center.y - v.y };
    return roundVec2(mirrored);
}

function activeAxes(sym: SymmetryState): SymmetryAxis[] {
    const axes: SymmetryAxis[] = [];
    if (sym.x) axes.push("x");
    if (sym.y) axes.push("y");
    return axes;
}

export const EPS = 1e-6;
export function sameVec(a: Vec2, b: Vec2): boolean {
    return Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;
}

/**
 * All distinct mirror positions of `v` for the active axes, EXCLUDING `v`
 * itself, reflected around `center` (the canvas center). X+Y active at a
 * generic point yields 3 mirrors (so 4 positions total with the original).
 * Positions coincident with `v` (e.g. a point on a mirror axis) or with each
 * other are deduplicated, so the count reflects how many distinct counterpart
 * nodes symmetry would touch.
 */
export function mirrorPositions(
    v: Vec2,
    sym: SymmetryState,
    center: Vec2,
): Vec2[] {
    const axes = activeAxes(sym);
    if (axes.length === 0) return [];
    let variants: Vec2[] = [{ ...v }];
    for (const axis of axes) {
        const next: Vec2[] = [];
        for (const variant of variants) {
            next.push(variant);
            next.push(mirrorVec(variant, axis, center));
        }
        variants = next;
    }
    const out: Vec2[] = [];
    for (const cand of variants) {
        if (sameVec(cand, v)) continue;
        if (out.some((o) => sameVec(o, cand))) continue;
        out.push(cand);
    }
    return out;
}

/**
 * One mirrored copy of a shape's geometry: the transformed `points` (input
 * order preserved) plus `flipped`, true when the mirror reverses handedness (an
 * odd number of axis reflections). `flipped` lets a shape whose look depends on
 * chord side / winding — e.g. an arc's bulge direction — invert that flag so the
 * copy is a true mirror image rather than a same-handed translation.
 */
export interface MirroredPointSet {
    points: Vec2[];
    flipped: boolean;
}

/**
 * Mirror an ordered list of points (a whole shape's defining geometry) under
 * each active mirror combination, returning one transformed copy per distinct
 * mirror — EXCLUDING the identity. Unlike `mirrorPositions`, every point is
 * reflected by the *same* combination so the shape keeps its form. With X+Y
 * active a generic shape yields 3 copies (mirror-X, mirror-Y, mirror-both).
 *
 * Combinations whose result is point-for-point identical to the input (a shape
 * lying entirely on a mirror axis), or identical to a copy already produced,
 * are dropped — so the count reflects how many distinct mirrored shapes
 * symmetry would actually add.
 */
export function mirrorPointSets(
    points: Vec2[],
    sym: SymmetryState,
    center: Vec2,
): MirroredPointSet[] {
    const axes = activeAxes(sym);
    if (axes.length === 0 || points.length === 0) return [];

    // Every non-empty subset of active axes is one mirror combination. With a
    // single axis that's just [axis]; with both it's [x], [y], and [x, y].
    const combos: SymmetryAxis[][] = [];
    const total = 1 << axes.length;
    for (let mask = 1; mask < total; mask += 1) {
        const combo: SymmetryAxis[] = [];
        for (let i = 0; i < axes.length; i += 1) {
            if (mask & (1 << i)) combo.push(axes[i]!);
        }
        combos.push(combo);
    }

    const sameSet = (a: Vec2[], b: Vec2[]): boolean =>
        a.length === b.length && a.every((p, i) => sameVec(p, b[i]!));

    const out: MirroredPointSet[] = [];
    for (const combo of combos) {
        const mirrored = points.map((p) => {
            let q = p;
            for (const axis of combo) q = mirrorVec(q, axis, center);
            return q;
        });
        if (sameSet(mirrored, points)) continue;
        if (out.some((existing) => sameSet(existing.points, mirrored))) continue;
        // Each single-axis reflection flips handedness; an even count restores it.
        out.push({ points: mirrored, flipped: combo.length % 2 === 1 });
    }
    return out;
}

/** Default position-match tolerance (SVG units) for finding symmetric counterparts. */
export const SYMMETRY_MATCH_TOLERANCE = 1.5;

/**
 * Find the first editable (non spawn-clone) node whose center sits within
 * `tolerance` of `pos`, optionally skipping ids in `exclude`. Used to detect a
 * symmetric counterpart already present at a mirrored position — symmetry now
 * acts on matching nodes by geometry rather than via any stored mirror link.
 */
export function findNodeNearPosition(
    nodes: DiagramNode[],
    pos: Vec2,
    tolerance = SYMMETRY_MATCH_TOLERANCE,
    exclude?: ReadonlySet<string>,
): DiagramNode | undefined {
    const tol2 = tolerance * tolerance;
    let best: DiagramNode | undefined;
    let bestDist = Infinity;
    for (const n of nodes) {
        if (n.spawnedFrom) continue;
        if (exclude?.has(n.id)) continue;
        const dx = n.position.x - pos.x;
        const dy = n.position.y - pos.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= tol2 && d2 < bestDist) {
            bestDist = d2;
            best = n;
        }
    }
    return best;
}

/**
 * Find the first shape whose ordered defining points all sit within `tolerance`
 * of `points` (same length, point-for-point), optionally skipping ids in
 * `exclude`. The shape geometry of each candidate is read via `getPoints`. Used
 * to locate a symmetric counterpart shape (line, arc, rect, polygon, ellipse
 * center) by geometry — the shape analogue of `findNodeNearPosition`, so that
 * transform tools (e.g. rotate) can drag a mirror copy along with its original
 * without any stored mirror link.
 */
export function findShapeNearPoints<T extends { id: string }>(
    shapes: T[],
    points: Vec2[],
    getPoints: (shape: T) => Vec2[],
    tolerance = SYMMETRY_MATCH_TOLERANCE,
    exclude?: ReadonlySet<string>,
): T | undefined {
    const tol2 = tolerance * tolerance;
    let best: T | undefined;
    let bestDist = Infinity;
    for (const shape of shapes) {
        if (exclude?.has(shape.id)) continue;
        const candidate = getPoints(shape);
        if (candidate.length !== points.length) continue;
        let total = 0;
        let ok = true;
        for (let i = 0; i < points.length; i += 1) {
            const dx = candidate[i]!.x - points[i]!.x;
            const dy = candidate[i]!.y - points[i]!.y;
            const d2 = dx * dx + dy * dy;
            if (d2 > tol2) {
                ok = false;
                break;
            }
            total += d2;
        }
        if (ok && total < bestDist) {
            bestDist = total;
            best = shape;
        }
    }
    return best;
}

